// Support tickets — customer-side list + inline thread expand. Same shape
// as Conversations: a row click toggles an inline detail row that fetches
// the full message thread on demand. Sidebar sits in Operate.
//
// Flow:
//   • Empty state when there are no tickets — primary CTA to file the
//     first one.
//   • "New ticket" Dialog (NewTicketDialog) for creation; reuses
//     <ErrorSummary> the same way the agent form does.
//   • Inline expand renders <TicketThread> with the description card,
//     the reply list, an "Add reply" textarea, and a "Mark as closed"
//     button when the ticket is in `resolved`.

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, LifeBuoy, Lock, Plus } from 'lucide-react';
import type { SupportPriority, SupportStatus, SupportTicket } from '@cortex/shared';
import { useApiClient } from '../../lib/providers.js';
import { supportApi, SUPPORT_QUERY_KEY } from '../../lib/api/support.js';
import { usageApi, USAGE_QUERY_KEY } from '../../lib/api/usage.js';
import { formatDate, formatDateTime } from '../../lib/format-date.js';
import { Badge, type BadgeProps } from '../../components/ui/badge.js';
import { Button } from '../../components/ui/button.js';
import { Card, CardContent } from '../../components/ui/card.js';
import { EmptyState } from '../../components/ui/empty-state.js';
import { ErrorRetry } from '../../components/ui/error-retry.js';
import { PageHeader } from '../../components/ui/page-header.js';
import { Skeleton } from '../../components/ui/skeleton.js';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table.js';
import { NewTicketDialog } from './create-dialog.js';
import { TicketThread } from './ticket-thread.js';

// Status / priority → Badge variant mapping. Mirrors the convention used by
// audit-log (action namespace → variant) — keeps the visual vocabulary
// consistent across the dashboard.
type BadgeVariant = NonNullable<BadgeProps['variant']>;

const STATUS_VARIANT: Record<SupportStatus, BadgeVariant> = {
  open: 'default',
  in_progress: 'warning',
  waiting_customer: 'secondary',
  resolved: 'success',
  closed: 'muted',
};

const STATUS_LABEL: Record<SupportStatus, string> = {
  open: 'Open',
  in_progress: 'In progress',
  waiting_customer: 'Awaiting your reply',
  resolved: 'Resolved',
  closed: 'Closed',
};

const PRIORITY_VARIANT: Record<SupportPriority, BadgeVariant> = {
  low: 'muted',
  normal: 'secondary',
  high: 'warning',
  urgent: 'destructive',
};

export function SupportListPage() {
  const api = useApiClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // Plan-gate: support tickets are a paid feature (Starter/Pro). Free users
  // see an upsell EmptyState in place of the create button + tickets list.
  // We share the USAGE_QUERY_KEY cache with billing/upgrade + create-dialog
  // so the plan check is free if the user has visited those pages already.
  const usage = useQuery({
    queryKey: USAGE_QUERY_KEY,
    queryFn: () => usageApi.summary(api),
  });
  const planCode = usage.data?.plan.code;
  // Default to gating-on (free) when the query hasn't resolved — avoids a
  // flash of the paid UI before the plan check completes. Once data lands,
  // paid users immediately see the full surface.
  const isFreeOrLoading = planCode === undefined || planCode === 'free';

  const list = useQuery({
    queryKey: SUPPORT_QUERY_KEY,
    queryFn: () => supportApi.list(api),
    // Skip the tickets fetch entirely for free users — they cannot create
    // any so the list is always empty AND the API may itself reject the
    // call (defense-in-depth: server-side gate is a follow-up).
    enabled: planCode !== undefined && planCode !== 'free',
  });

  if (planCode === 'free') {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Support"
          description="File tickets and track replies in one place — included on Starter and Pro plans."
        />
        <EmptyState
          icon={Lock}
          title="Support tickets are on Starter & Pro"
          description="Upgrade to file tickets and get in-app responses from the Cortex team. Free-plan questions are answered through the docs and quickstart."
          action={
            <Button asChild>
              <Link to="/billing/upgrade">Upgrade plan</Link>
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Support"
        description="File a ticket and track the conversation here. Replies appear on this page — there's no email thread to chase."
        actions={
          <Button onClick={() => { setCreateOpen(true); }} disabled={isFreeOrLoading}>
            <Plus className="h-4 w-4" aria-hidden />
            New ticket
          </Button>
        }
      />

      {list.isLoading && <TableSkeleton />}
      {list.isError && (
        <ErrorRetry
          title="Could not load tickets"
          error={list.error}
          onRetry={() => void list.refetch()}
        />
      )}
      {list.data?.tickets.length === 0 && (
        <EmptyState
          icon={LifeBuoy}
          title="No tickets yet"
          description="Stuck on something? File a ticket and we'll respond on this page."
          action={
            <Button onClick={() => { setCreateOpen(true); }}>
              <Plus className="h-4 w-4" aria-hidden />
              Open your first ticket
            </Button>
          }
        />
      )}
      {list.data && list.data.tickets.length > 0 && (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8" aria-hidden />
                  <TableHead>Subject</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Priority</TableHead>
                  <TableHead>Last activity</TableHead>
                  <TableHead>Filed</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {list.data.tickets.map((t) => (
                  <TicketRow
                    key={t.id}
                    ticket={t}
                    expanded={expandedId === t.id}
                    onToggle={() => {
                      setExpandedId(expandedId === t.id ? null : t.id);
                    }}
                  />
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <NewTicketDialog
        open={createOpen}
        onClose={() => { setCreateOpen(false); }}
        onCreated={(detail) => {
          // The Dialog already invalidates SUPPORT_QUERY_KEY on success;
          // expand the new row so the user lands on their thread.
          setExpandedId(detail.ticket.id);
        }}
      />
    </div>
  );
}

function TableSkeleton() {
  return (
    <Card>
      <CardContent className="p-0">
        <div className="divide-y">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="grid grid-cols-6 items-center gap-4 px-4 py-3">
              <Skeleton className="col-span-2 h-4" />
              <Skeleton className="h-5 w-16 rounded-full" />
              <Skeleton className="h-5 w-12 rounded-full" />
              <Skeleton className="h-4" />
              <Skeleton className="h-4" />
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

interface TicketRowProps {
  ticket: SupportTicket;
  expanded: boolean;
  onToggle: () => void;
}

function TicketRow({ ticket, expanded, onToggle }: TicketRowProps) {
  const Icon = expanded ? ChevronDown : ChevronRight;
  return (
    <>
      <TableRow onClick={onToggle} className="cursor-pointer" aria-expanded={expanded}>
        <TableCell className="w-8">
          <button
            type="button"
            aria-label={expanded ? 'Collapse thread' : 'Expand thread'}
            className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
            onClick={(e) => {
              e.stopPropagation();
              onToggle();
            }}
          >
            <Icon className="h-4 w-4" aria-hidden />
          </button>
        </TableCell>
        <TableCell>
          <div className="font-medium">{ticket.subject}</div>
          <div className="font-mono text-[11px] text-muted-foreground">
            #{ticket.id.slice(0, 8)}
          </div>
        </TableCell>
        <TableCell>
          <Badge variant={STATUS_VARIANT[ticket.status]}>
            {STATUS_LABEL[ticket.status]}
          </Badge>
        </TableCell>
        <TableCell>
          <Badge variant={PRIORITY_VARIANT[ticket.priority]} className="capitalize">
            {ticket.priority}
          </Badge>
        </TableCell>
        <TableCell className="text-xs text-muted-foreground">
          {formatDateTime(ticket.lastMessageAt)}
        </TableCell>
        <TableCell className="text-xs text-muted-foreground">
          {formatDate(ticket.createdAt)}
        </TableCell>
      </TableRow>
      {expanded && (
        <TableRow className="bg-muted/20 hover:bg-muted/20">
          <TableCell colSpan={6} className="p-4">
            <TicketThread id={ticket.id} />
          </TableCell>
        </TableRow>
      )}
    </>
  );
}
